import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Info, Brain, Heart, Shield, Activity, Sparkles, RefreshCw, ChevronDown, ChevronUp, Target, Wand2, Save, Printer, Download, Check, Link as LinkIcon, TrendingUp, FileText, Share2, Compass, LogOut } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, BarChart, Bar, Legend, ReferenceLine } from 'recharts';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';
import logger from '../../lib/logger';
import ShareReportCardDialog from '../../components/ShareReportCardDialog';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { DeferredChart, ZoomableTrendChart } from '../../components/people-engine/ZoomableTrendChart';
import { JourneyHeroStats } from '../../components/people-engine/JourneyHeroStats';
import { JourneyConversionChain } from '../../components/people-engine/JourneyConversionChain';
import { NavigationReadinessTrend } from '../../components/people-engine/NavigationReadinessTrend';
import { FrictionPanel } from '../../components/people-engine/FrictionPanel';
import { getLightSectionTheme } from '../../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hoisted to module scope to avoid re-creating the prop object on each render.
const SKILL_BAR_MARGIN = { left: 120 };

// Deep-dive tab metadata — keyed by data stream, ordered by journey
// stage (Capability → Visibility → Confidence → Coordination).
const componentMeta = {
  capability: { icon: Brain, color: '#6366f1', label: 'Capability (C)', journey: 'Capability' },
  lei: { icon: Activity, color: '#8b5cf6', label: 'Learning Effectiveness (LE)', journey: 'Visibility' },
  motivation: { icon: Heart, color: '#f59e0b', label: 'Motivation (M)', journey: 'Confidence' },
  retention: { icon: Shield, color: '#10b981', label: 'Retention (R)', journey: 'Coordination' },
};

const InfoTooltip = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline ml-1" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs"><p>{text}</p></TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// ──── Motivation Driver info content ────
const MOTIVATION_DRIVER_INFO = {
  // Intrinsic
  'Desire for meaning & purpose': { impact: 'Purpose-driven employees apply learning because they believe it matters. This is the strongest predictor of sustained behavior change — it outlasts any incentive program.', risk: 'Low purpose scores often indicate misalignment between role, training, and organizational mission. Learners may complete training but see no reason to apply it.', opportunity: 'Connect every learning program explicitly to organizational purpose and individual impact. Show learners how their growth contributes to meaningful outcomes.' },
  'Passion for the role': { impact: 'Role passion creates natural motivation to build expertise. Passionate employees seek learning proactively and apply it without external pressure.', risk: 'Low passion may signal role misfit — training won\'t overcome a fundamental engagement problem. Consider whether people are in the right roles before investing in upskilling.', opportunity: 'Align learning paths with career aspirations. Give people autonomy in choosing what to learn and how to apply it.' },
  'Growth & development motivation': { impact: 'Growth-oriented employees see learning as personally valuable, not just organizationally mandated. They transfer more knowledge because they want to improve.', risk: 'If growth scores are low, learners may view training as a checkbox rather than a development opportunity, dramatically reducing transfer rates.', opportunity: 'Create visible career pathways tied to skill development. Show how specific capabilities unlock advancement and new responsibilities.' },
  'Pride in work': { impact: 'Pride drives quality of application. Employees who take pride in their work are more likely to apply new skills carefully and consistently, improving L3 behavior change scores.', risk: 'Low pride often correlates with low psychological safety — people won\'t try new approaches if they fear failure or judgment.', opportunity: 'Recognize and celebrate skill application (not just completion). Create environments where experimenting with new approaches is valued.' },
  // Extrinsic
  'Positive work environment': { impact: 'The environment determines whether conditions support learning application. Poor environments (high stress, inadequate tools, no time) block transfer regardless of capability.', risk: 'This is the most common hidden barrier — organizations invest in training but don\'t change the environment that prevents application.', opportunity: 'Audit post-training environments: Do learners have time, tools, and permission to apply new skills? Remove systemic barriers before scaling training.' },
  'Leadership quality': { impact: 'Leaders model the behaviors training tries to build. If leaders don\'t demonstrate or value the skills being taught, learners receive conflicting signals.', risk: 'Low leadership quality scores indicate a culture problem that training alone cannot solve. It suppresses the entire M score.', opportunity: 'Invest in leader-as-coach programs. Ensure leaders complete the same training and visibly apply it. Leader endorsement is the #1 amplifier of training transfer.' },
  'Recognition': { impact: 'Recognition reinforces new behaviors in the critical 30-90 day window after training, when habits are forming. Without it, old behaviors return.', risk: 'If recognition is low, people receive no positive feedback for changing behavior — the effort of applying new skills feels unrewarded.', opportunity: 'Build structured recognition for skill application (not just training completion). Peer recognition, manager shout-outs, and team showcases are low-cost, high-impact.' },
  'Compensation': { impact: 'Compensation alignment signals organizational commitment. If pay and promotion don\'t reward the capabilities being built, the message is "we don\'t really value this."', risk: 'Low compensation scores create cynicism — learners may perceive training as a cost-cutting substitute for fair pay rather than genuine development.', opportunity: 'Link capability development to tangible career benefits. Certification bonuses, skill-based pay bands, or promotion criteria tied to demonstrated competencies.' },
  'Work-life balance': { impact: 'Overworked employees lack the cognitive bandwidth to learn and apply new skills. Balance directly affects learning absorption and post-training energy for behavior change.', risk: 'If balance scores are very low, adding training requirements can actually decrease performance — it becomes one more demand on an already-stretched workforce.', opportunity: 'Integrate learning into the workday rather than adding it on top. Micro-learning, on-the-job application, and protected learning time respect work-life balance.' },
  'Belief in vision/mission': { impact: 'Mission belief provides the context for why skills matter. It connects individual capability to collective purpose, strengthening both intrinsic and extrinsic motivation.', risk: 'Low mission belief suggests strategic disconnection — people may not understand or trust where the organization is heading, making all development feel pointless.', opportunity: 'Explicitly connect learning programs to strategic vision in program design and communications. Show how capability building serves the mission.' },
};

const MotivationDriverRow = ({ driver, type, color }) => {
  const [showInfo, setShowInfo] = useState(false);
  const info = MOTIVATION_DRIVER_INFO[driver];
  return (
    <li className={`text-sm text-slate-600 pl-3 border-l-2 border-${color}-300`}>
      <div className="flex items-center gap-1">
        <span>{driver}</span>
        {info && (
          <button onClick={() => setShowInfo(!showInfo)} className={`text-slate-400 hover:text-indigo-500 transition-colors`} data-testid={`driver-info-${driver.slice(0, 15)}`}>
            <Info className="h-3 w-3" />
          </button>
        )}
      </div>
      {showInfo && info && (
        <div className="mt-1.5 mb-1 p-2 bg-indigo-50 border border-indigo-100 rounded text-xs leading-relaxed space-y-0.5">
          <p className="text-indigo-800"><strong>Impact on M:</strong> {info.impact}</p>
          <p className="text-indigo-700"><strong>Risk if low:</strong> {info.risk}</p>
          <p className="text-indigo-600"><strong>Opportunity:</strong> {info.opportunity}</p>
        </div>
      )}
    </li>
  );
};

/**
 * Motivation trend chart with a Y-axis zoom slider.
 * The three lines (Total / Intrinsic / Extrinsic) often cluster within
 * a 5-10 pp band, making them visually overlap on a 0–100% axis.
 * The zoom control lets the user tighten the Y-axis window so small
 * differences between drivers become visible.
 *
 * Modes:
 *   • Auto — fits to data range with 3 pp padding (default)
 *   • Full — locked 0–100%
 *   • Custom — dual-handle slider for min/max (1 pp granularity)
 */
const MotivationTrendChart = ({ data }) => (
  <ZoomableTrendChart
    data={data}
    title="Confidence (Motivation) Score Trend"
    testIdPrefix="motivation"
    lines={[
      { dataKey: 'total',     name: 'Total',     stroke: '#f59e0b', strokeWidth: 2.5 },
      { dataKey: 'intrinsic', name: 'Intrinsic', stroke: '#22c55e', strokeWidth: 1.5, dashed: true, dot: false },
      { dataKey: 'extrinsic', name: 'Extrinsic', stroke: '#3b82f6', strokeWidth: 1.5, dashed: true, dot: false },
    ]}
  />
);

// ──── LEI Pillar info content ────
const LEI_PILLAR_INFO = {
  'Learning Access & Engagement': { what: 'Measures how well your learning system reaches and engages the workforce — training scope, SME access, community learning, voluntary participation, and tech utilization.', influence: 'Access & Engagement primarily drives the Motivation (M) component. If people can\'t access or don\'t engage with learning, no capability change occurs.', risk: 'Low scores here mean your learning investment has a reach problem — programs exist but aren\'t being consumed. This caps the entire value chain.', opportunity: 'Improving access is often the highest-ROI lever because it amplifies all other learning investments. Focus on reducing friction in learning access.' },
  'Learning Performance & Outcomes': { what: 'Measures training quality through assessment scores, pass rates, mastery levels, and learner satisfaction (NPS). This is the core L2 effectiveness signal and carries the highest weight (30%).', influence: 'Performance & Outcomes directly drives the Capability (C) component. Higher quality training = larger skill gains = higher C score.', risk: 'Low scores mean training isn\'t actually building skills, regardless of how many people complete it. Completion does not equal capability.', opportunity: 'Invest in instructional design quality, practice-based learning, and rigorous assessment. Small quality improvements multiply across all participants.' },
  'Workforce Capability & Readiness': { what: 'Measures actual skill outcomes — assessment scores, skills gap closure, certification depth, and readiness index. The "proof" that learning built capability.', influence: 'Workforce Capability has a strong direct influence on PE because it feeds both C and the overall PE formula through demonstrated competency.', risk: 'Low skills scores despite high quality training suggests the wrong people are being trained, or skills aren\'t aligned to role requirements.', opportunity: 'Align training directly to skills frameworks and role requirements. Measure what matters — not course completion, but demonstrated competency.' },
  'Operational Efficiency': { what: 'Measures operational efficiency — cost per learner, scheduling velocity, and resource utilization. How efficiently your learning investment converts to outcomes.', influence: 'Efficiency doesn\'t directly increase PE, but it determines how much PE improvement you get per dollar invested. Higher efficiency = better learning ROI.', risk: 'Low efficiency means you\'re spending more than necessary for each unit of capability gain, weakening your business case for future investment.', opportunity: 'Optimize delivery costs, consolidate redundant programs, and improve scheduling. Efficiency gains free budget for higher-impact initiatives.' },
  'Business Impact & Value Realization': { what: 'Measures how tightly learning programs are aligned to business KPIs and how effectively learning value is being realized. Despite lowest weight (10%), it validates the entire chain.', influence: 'Business Impact validates whether all upstream LEI improvements are actually reaching business outcomes. It\'s the "proof" that LEI drives KPI and ROI.', risk: 'Low scores here mean your learning programs may be effective in isolation but aren\'t connected to business strategy — the ROI story has no evidence.', opportunity: 'Strengthen KPI alignment in program design. Use the correlation analysis module to identify which LEI measures have the strongest KPI relationships.' },
};

// Simple markdown renderer for AI narrative
const MarkdownRenderer = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  for (const line of lines) {
    i++;
    if (line.startsWith('## ')) {
      elements.push(<h3 key={`h3-${i}`} className="text-base font-semibold text-slate-800 mt-4 mb-2">{line.replace('## ', '')}</h3>);
    } else if (line.startsWith('### ')) {
      elements.push(<h4 key={`h4-${i}`} className="text-sm font-semibold text-slate-700 mt-3 mb-1">{line.replace('### ', '')}</h4>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <li key={`li-${i}`} className="text-sm text-slate-600 ml-4 mb-1 list-disc">
          {line.replace(/^[-*]\s/, '').split('**').map((part, j) =>
            j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
          )}
        </li>
      );
    } else if (line.match(/^\d+\.\s/)) {
      elements.push(
        <li key={`ol-${i}`} className="text-sm text-slate-600 ml-4 mb-1 list-decimal">
          {line.replace(/^\d+\.\s/, '').split('**').map((part, j) =>
            j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
          )}
        </li>
      );
    } else if (line.trim()) {
      elements.push(
        <p key={`p-${i}`} className="text-sm text-slate-600 mb-2">
          {line.split('**').map((part, j) =>
            j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
          )}
        </p>
      );
    }
  }
  return <div>{elements}</div>;
};

// ──── LEI Pillar Row with info ────
const LEIPillarRow = ({ pillar }) => {
  const [showInfo, setShowInfo] = useState(false);
  const info = LEI_PILLAR_INFO[pillar.name];
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500 w-8 flex-shrink-0">{Math.round(pillar.weight * 100)}%</span>
        <div className="flex-1">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm text-slate-700">{pillar.name}</span>
            {info && (
              <button onClick={() => setShowInfo(!showInfo)} className={`text-slate-400 hover:text-indigo-500 transition-colors`} data-testid={`lei-pillar-info-${pillar.name.slice(0, 10)}`}>
                <Info className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-violet-500" style={{ width: `${pillar.weight * 100}%` }} />
          </div>
        </div>
      </div>
      {showInfo && info && (
        <div className="ml-11 mt-1.5 mb-1 p-2 bg-indigo-50 border border-indigo-100 rounded text-xs leading-relaxed space-y-0.5">
          <p className="text-indigo-800"><strong>What it measures:</strong> {info.what}</p>
          <p className="text-indigo-700"><strong>Influence on PE:</strong> {info.influence}</p>
          <p className="text-indigo-700"><strong>Risk if low:</strong> {info.risk}</p>
          <p className="text-indigo-600"><strong>Opportunity:</strong> {info.opportunity}</p>
        </div>
      )}
    </div>
  );
};

export default function PEDiagnosticPage() {
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initComponent = searchParams.get('component') || 'capability';
  const initOrg = searchParams.get('organization_id') || searchParams.get('org') || viewAsOrg?.id || '';

  const [activeTab, setActiveTab] = useState(initComponent);
  const [selectedOrg, setSelectedOrg] = useState(initOrg);
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [diagnostic, setDiagnostic] = useState(null);
  // PE composite health snapshot (powers the hero stats — capability /
  // motivation / retention / overall PE — independent of the active tab).
  const [peHealth, setPeHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  // loadError shape: { status: number|null, message: string, retriable: boolean } | null
  // Retriable = true for network failures + 5xx + 408/429; false for 4xx auth/forbidden/not-found.
  const [loadError, setLoadError] = useState(null);
  // Bump to force a re-fire of the fetchDiagnostic effect on Retry click.
  const [retryNonce, setRetryNonce] = useState(0);

  // AI Narrative state
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [showNarrative, setShowNarrative] = useState(false);

  const headers = useCallback(() => {
    const h = { 'Authorization': `Bearer ${secureStorage.getItem('session_token')}` };
    if (viewAsOrg?.id) h['X-View-As-Org'] = viewAsOrg.id;
    return h;
  }, [viewAsOrg]);

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await authedFetch(`${API_URL}/api/people-engine/organizations`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          const orgs = data.organizations || [];
          setOrganizations(orgs);
          setSelectedOrg(prev => {
            if (prev) return prev;
            // Belt-and-suspenders: prefer the currently-previewed org if it's
            // in the returned list, BEFORE falling back to orgs[0]. Fixes the
            // edge case where a super-admin's own org happens to sort first
            // but they're actually previewing a different demo tenant.
            if (viewAsOrg?.id && orgs.some(o => o.id === viewAsOrg.id)) {
              return viewAsOrg.id;
            }
            return orgs[0]?.id || '';
          });
        }
      } catch (err) { logger.error('Failed to fetch organizations:', err); }
    };
    fetchOrgs();
  }, [headers, viewAsOrg?.id]);

  // Keep selectedOrg in sync with the currently-previewed org.
  // Fixes the "direct-nav returns empty" bug: on first render viewAsOrg
  // may still be null (hydrates async from localStorage), so fetchOrgs
  // would otherwise pick the super-admin's real org as the default —
  // which has no PE diagnostic data. Re-snapping whenever viewAsOrg
  // changes guarantees direct-nav and preview-switches both work.
  useEffect(() => {
    if (viewAsOrg?.id && viewAsOrg.id !== selectedOrg) {
      setSelectedOrg(viewAsOrg.id);
    }
  }, [viewAsOrg?.id, selectedOrg]);

  useEffect(() => {
    if (!selectedOrg) return;
    const fetchCohorts = async () => {
      try {
        const res = await authedFetch(`${API_URL}/api/people-engine/cohorts?organization_id=${selectedOrg}`, { headers: headers() });
        if (res.ok) setCohorts((await res.json()).cohorts || []);
      } catch (err) { logger.error('Failed to fetch cohorts:', err); }
    };
    fetchCohorts();
  }, [selectedOrg, headers]);

  // Pull the PE composite health (Capability/Motivation/Retention/PE)
  // once per (org, cohort) combo to power the hero stats. Failures
  // are swallowed silently — hero stats simply hide if the data
  // never arrives, the diagnostic tab content below still renders.
  useEffect(() => {
    if (!selectedOrg) return;
    const fetchPeHealth = async () => {
      try {
        const params = new URLSearchParams({ organization_id: selectedOrg });
        if (selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
        const res = await authedFetch(
          `${API_URL}/api/people-engine/health?${params}`,
          { headers: headers() },
        );
        if (res.ok) {
          setPeHealth(await res.json());
        } else {
          setPeHealth(null);
        }
      } catch (err) {
        logger.debug?.('PE health fetch (hero) failed:', err);
        setPeHealth(null);
      }
    };
    fetchPeHealth();
  }, [selectedOrg, selectedCohort, headers, retryNonce]);

  useEffect(() => {
    if (!selectedOrg) return;
    const fetchDiagnostic = async () => {
      setLoading(true);
      setLoadError(null);
      // Reset stale tab data so a previous tab's cards never linger
      // on screen if the new request fails or returns nothing.
      setDiagnostic(null);
      setNarrative(null);
      setShowNarrative(false);
      try {
        const params = new URLSearchParams({ organization_id: selectedOrg });
        if (selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
        const res = await authedFetch(`${API_URL}/api/people-engine/diagnostic/${activeTab}?${params}`, { headers: headers() });
        if (res.ok) {
          setDiagnostic(await res.json());
        } else {
          // Surface non-OK responses (401, 403, 500…) instead of leaving
          // the page silently blank below the tabs. 4xx are typically
          // user-fixable (re-login, switch org) and not retriable from
          // here; 5xx and 408/429 should offer a Retry button.
          const detail = await res.text().catch(() => '');
          const retriable = res.status >= 500 || res.status === 408 || res.status === 429;
          const msg = `Failed to load ${activeTab} diagnostic (HTTP ${res.status})`;
          logger.error(msg, detail);
          setLoadError({ status: res.status, message: msg, retriable });
          toast.error(msg);
        }
      } catch (e) {
        // Network / abort / parse error — always retriable.
        logger.error('Diagnostic fetch threw', e);
        setLoadError({ status: null, message: 'Failed to load diagnostic data', retriable: true });
        toast.error('Failed to load diagnostic data');
      }
      setLoading(false);
    };
    fetchDiagnostic();
  }, [activeTab, selectedOrg, selectedCohort, headers, retryNonce]);

  const generateNarrative = async () => {
    setNarrativeLoading(true);
    setShowNarrative(true);
    try {
      const params = new URLSearchParams({ organization_id: selectedOrg });
      if (selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
      const res = await authedFetch(`${API_URL}/api/people-engine/diagnostic/${activeTab}/narrative?${params}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setNarrative(data);
      } else {
        toast.error('Failed to generate narrative');
      }
    } catch (e) {
      toast.error('Failed to generate AI narrative');
    }
    setNarrativeLoading(false);
  };

  // ── Per-Skill Action Plan (GPT-5.2) ───────────────────
  const [planSkill, setPlanSkill] = useState(null);
  const [planText, setPlanText] = useState('');
  const [planLoading, setPlanLoading] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [planSavedId, setPlanSavedId] = useState(null);

  const openSkillPlan = async (skill) => {
    setPlanSkill(skill);
    setPlanText('');
    setPlanSavedId(null);
    setPlanLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/diagnostic/capability/skill-action-plan`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: selectedOrg,
          cohort_id: selectedCohort === 'all' ? null : selectedCohort,
          skill_name: skill.skill_name,
          required_level: skill.required_level,
          achieved_level: skill.achieved_level,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPlanText(data.action_plan || '');
      } else {
        toast.error('Failed to generate action plan');
        setPlanText('Unable to generate action plan at this time.');
      }
    } catch {
      toast.error('Failed to generate action plan');
      setPlanText('Unable to generate action plan at this time.');
    }
    setPlanLoading(false);
  };

  const savePlanToBacklog = async () => {
    if (!planSkill || !planText || planSavedId) return;
    setPlanSaving(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/intervention-backlog`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: selectedOrg,
          cohort_id: selectedCohort === 'all' ? null : selectedCohort,
          cohort_name: selectedCohort === 'all' ? 'All Cohorts' : (cohorts.find(c => c.id === selectedCohort)?.name || null),
          skill_name: planSkill.skill_name,
          required_level: planSkill.required_level,
          achieved_level: planSkill.achieved_level,
          action_plan: planText,
          model: 'gpt-5.2',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPlanSavedId(data.id);
        toast.success('Saved to Program Backlog');
      } else {
        toast.error('Failed to save to backlog');
      }
    } catch {
      toast.error('Failed to save to backlog');
    }
    setPlanSaving(false);
  };

  const exportPlanMarkdown = () => {
    if (!planSkill || !planText) return;
    const org = organizations.find(o => o.id === selectedOrg)?.name || selectedOrg;
    const header = `# Action Plan — ${planSkill.skill_name}\n\n` +
      `- **Organization:** ${org}\n` +
      `- **Required proficiency:** ${planSkill.required_level?.toFixed(1)}/5\n` +
      `- **Achieved proficiency:** ${planSkill.achieved_level?.toFixed(1)}/5\n` +
      `- **Gap:** ${Math.max(0, (planSkill.required_level || 0) - (planSkill.achieved_level || 0)).toFixed(2)} points\n` +
      `- **Generated:** ${new Date().toISOString()}\n\n---\n\n`;
    const blob = new Blob([header + planText], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const slug = planSkill.skill_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.href = url;
    a.download = `action-plan-${slug}-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Execution Impact (linked program drill-down) ──────
  const [impactSkill, setImpactSkill] = useState(null); // { skill_name, program_id, program_name }
  const [impactData, setImpactData] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [reportPosting, setReportPosting] = useState(false);
  const [reportCaseId, setReportCaseId] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const openExecutionImpact = async (skill_name, linked) => {
    setImpactSkill({ skill_name, program_id: linked.id, program_name: linked.name });
    setImpactData(null);
    setReportCaseId(null);
    setImpactLoading(true);
    try {
      const params = new URLSearchParams({
        program_id: linked.id,
        skill_name,
        organization_id: selectedOrg,
      });
      const res = await authedFetch(`${API_URL}/api/people-engine/diagnostic/capability/execution-impact?${params}`, { headers: headers() });
      if (res.ok) {
        setImpactData(await res.json());
      } else {
        toast.error('Failed to load execution impact');
      }
    } catch {
      toast.error('Failed to load execution impact');
    }
    setImpactLoading(false);
  };

  const postImpactToReportCard = async () => {
    if (!impactSkill || reportCaseId) return;
    setReportPosting(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/diagnostic/capability/execution-impact/post-to-report-card`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program_id: impactSkill.program_id,
          skill_name: impactSkill.skill_name,
          organization_id: selectedOrg,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setReportCaseId(data.case_id);
        toast.success('Posted to Business Case Builder');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to post report');
      }
    } catch {
      toast.error('Failed to post report');
    }
    setReportPosting(false);
  };

  const printPlan = () => {    // A dedicated print window decouples from the app's layout/nav and avoids
    // @media print quirks in a modal-heavy SPA.
    if (!planSkill || !planText) return;
    const org = organizations.find(o => o.id === selectedOrg)?.name || selectedOrg;
    const gap = Math.max(0, (planSkill.required_level || 0) - (planSkill.achieved_level || 0)).toFixed(2);

    // Minimal markdown → HTML for print (same subset as MarkdownRenderer).
    const escape = (s) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]);
    const boldify = (s) => escape(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    const lines = planText.split('\n');
    const htmlParts = [];
    for (const raw of lines) {
      const line = raw.trimEnd();
      if (!line.trim()) { htmlParts.push(''); continue; }
      if (line.startsWith('## ')) htmlParts.push(`<h2>${boldify(line.slice(3))}</h2>`);
      else if (line.startsWith('### ')) htmlParts.push(`<h3>${boldify(line.slice(4))}</h3>`);
      else if (line.startsWith('- ') || line.startsWith('* ')) htmlParts.push(`<li>${boldify(line.slice(2))}</li>`);
      else if (/^\d+\.\s/.test(line)) htmlParts.push(`<li>${boldify(line.replace(/^\d+\.\s/, ''))}</li>`);
      else htmlParts.push(`<p>${boldify(line)}</p>`);
    }
    // Wrap consecutive <li> blocks into <ul>
    const joined = htmlParts.join('\n').replace(/(<li>[\s\S]*?<\/li>(?:\s*<li>[\s\S]*?<\/li>)*)/g, '<ul>$1</ul>');

    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) { toast.error('Pop-up blocked. Allow pop-ups to print.'); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Action Plan — ${escape(planSkill.skill_name)}</title>
      <style>
        body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; color:#0f172a; padding:32px; line-height:1.55; max-width: 720px; margin: 0 auto; }
        h1 { font-size: 20px; margin: 0 0 6px; }
        h2 { font-size: 14px; color:#4f46e5; margin: 20px 0 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
        h3 { font-size: 13px; color:#334155; margin: 14px 0 4px; }
        .meta { font-size: 12px; color:#64748b; margin-bottom: 16px; }
        .meta dt { display:inline; font-weight:600; color:#334155; }
        .meta dd { display:inline; margin:0 16px 0 4px; }
        p { font-size: 12.5px; margin: 6px 0; }
        ul { font-size: 12.5px; padding-left: 20px; margin: 6px 0; }
        li { margin: 3px 0; }
        .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 10px; color:#94a3b8; }
        @media print { body { padding: 16px; } }
      </style></head><body>
      <h1>Action Plan — ${escape(planSkill.skill_name)}</h1>
      <dl class="meta">
        <dt>Organization:</dt><dd>${escape(org)}</dd>
        <dt>Required:</dt><dd>${planSkill.required_level?.toFixed(1)}/5</dd>
        <dt>Achieved:</dt><dd>${planSkill.achieved_level?.toFixed(1)}/5</dd>
        <dt>Gap:</dt><dd>${gap} pts</dd>
      </dl>
      ${joined}
      <div class="footer">Generated ${new Date().toLocaleString()} · Seertech · GPT-5.2</div>
      </body></html>`);
    w.document.close();
    w.focus();
    // Give layout a tick before triggering print.
    setTimeout(() => { try { w.print(); } catch { /* user-cancelled */ } }, 300);
  };

  const renderCapabilityContent = () => {
    if (!diagnostic) return null;
    const skills = diagnostic.skills_breakdown || [];
    const programsLinked = diagnostic.programs_linked || {};

    return (
      <div className="space-y-4">
        {skills.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Skills Gap Analysis (Latest Period)</CardTitle>
            </CardHeader>
            <CardContent>
              <DeferredChart height={300}>
                <BarChart data={skills} layout="vertical" margin={SKILL_BAR_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="skill_name" tick={{ fontSize: 10 }} width={110} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="required_level" name="Required" fill="#e2e8f0" radius={[0, 2, 2, 0]} />
                  <Bar dataKey="achieved_level" name="Achieved" fill="#6366f1" radius={[0, 2, 2, 0]} />
                </BarChart>
              </DeferredChart>

              {/* Per-skill action plan — click-through to GPT-5.2 intervention */}
              <div className="mt-6 border-t border-slate-100 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="h-4 w-4 text-indigo-500" />
                  <h4 className="text-sm font-semibold text-slate-800">Intervention Plans</h4>
                  <span className="text-xs text-slate-400">Click any skill to get a GPT-5.2 drafted action plan</span>
                </div>
                <div className="space-y-1.5" data-testid="skill-action-plan-list">
                  {[...skills]
                    .map((s) => ({ ...s, _gap: Math.max(0, (s.required_level || 0) - (s.achieved_level || 0)) }))
                    .sort((a, b) => b._gap - a._gap)
                    .map((s) => {
                      const gap = s._gap;
                      const tone = gap >= 1.5 ? 'bg-rose-50 border-rose-200 text-rose-700'
                                 : gap >= 0.8 ? 'bg-amber-50 border-amber-200 text-amber-700'
                                 : 'bg-emerald-50 border-emerald-200 text-emerald-700';
                      const linked = programsLinked[s.skill_name];
                      return (
                        <div
                          key={s.skill_name}
                          className="flex items-center justify-between gap-3 py-2 px-3 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1 flex-wrap">
                            <span className="text-sm text-slate-700 truncate">{s.skill_name}</span>
                            <span className="text-xs text-slate-500 tabular-nums flex-shrink-0">
                              {s.achieved_level?.toFixed(1)} / {s.required_level?.toFixed(1)}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border tabular-nums flex-shrink-0 ${tone}`}>
                              gap {gap.toFixed(2)}
                            </span>
                            {linked && (
                              <button
                                type="button"
                                className="text-xs font-medium px-2 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center gap-1 flex-shrink-0 cursor-pointer transition-colors"
                                title={`View execution impact for ${linked.name}`}
                                data-testid={`skill-program-linked-${s.skill_name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}`}
                                onClick={() => openExecutionImpact(s.skill_name, linked)}
                              >
                                <LinkIcon className="h-3 w-3" />
                                {linked.name}
                                <TrendingUp className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 flex-shrink-0"
                            onClick={() => openSkillPlan(s)}
                            data-testid={`skill-plan-btn-${s.skill_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                          >
                            <Wand2 className="h-3.5 w-3.5" />
                            Action Plan
                          </Button>
                        </div>
                      );
                    })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderMotivationContent = () => {
    if (!diagnostic) return null;
    const trend = diagnostic.trend || [];
    const drivers = diagnostic.drivers || {};

    // Normalize motivation data from 0-100 to 0-1 scale (consistent with other components)
    const normalizedTrend = trend.map(t => ({
      period: t.period,
      total: (t.total ?? 0) / 100,
      intrinsic: (t.intrinsic ?? 0) / 100,
      extrinsic: (t.extrinsic ?? 0) / 100,
    }));

    const latest = trend[trend.length - 1];
    const intrinsicPct = latest?.intrinsic ?? 0;
    const extrinsicPct = latest?.extrinsic ?? 0;
    const totalPct = latest?.total ?? 0;
    const balance = intrinsicPct > 0 && extrinsicPct > 0 ? Math.round((intrinsicPct / (intrinsicPct + extrinsicPct)) * 100) : 50;
    const stronger = intrinsicPct >= extrinsicPct ? 'Intrinsic' : 'Extrinsic';
    const weaker = intrinsicPct < extrinsicPct ? 'Intrinsic' : 'Extrinsic';
    const gap = Math.abs(intrinsicPct - extrinsicPct).toFixed(1);

    return (
      <div className="space-y-4">
        <MotivationTrendChart data={normalizedTrend} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Intrinsic vs Extrinsic comparison — replaces meaningless radar */}
          {latest && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Intrinsic vs Extrinsic Balance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-slate-500">Comparing the two motivation dimensions for the latest period. A healthy balance means both internal drive and external support are present.</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-emerald-700">Intrinsic (Purpose, Mastery, Growth)</span>
                      <span className="font-bold text-emerald-700">{intrinsicPct.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${intrinsicPct}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-blue-700">Extrinsic (Recognition, Career, Environment)</span>
                      <span className="font-bold text-blue-700">{extrinsicPct.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${extrinsicPct}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-amber-700">Combined Motivation Score</span>
                      <span className="font-bold text-amber-700">{totalPct.toFixed(1)}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${totalPct}%` }} />
                    </div>
                  </div>
                </div>
                <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-100 text-xs text-slate-600 leading-relaxed">
                  <strong>{stronger}</strong> motivation is currently stronger ({balance}% of combined score).
                  {parseFloat(gap) > 10
                    ? ` The ${gap}pp gap suggests ${weaker.toLowerCase()} factors need attention — ${weaker === 'Intrinsic' ? 'people may comply but lack genuine drive to apply learning' : 'the environment may not be reinforcing internal motivation'}.`
                    : ` The dimensions are well-balanced (${gap}pp gap), indicating both internal drive and external support are present.`
                  }
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Motivation Drivers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-emerald-700 mb-2">Intrinsic Drivers</h4>
                <ul className="space-y-1.5">
                  {(drivers.intrinsic || []).map((d, i) => (
                    <MotivationDriverRow key={`intrinsic-${i}`} driver={d} type="intrinsic" color="emerald" />
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-blue-700 mb-2">Extrinsic Drivers</h4>
                <ul className="space-y-1.5">
                  {(drivers.extrinsic || []).map((d, i) => (
                    <MotivationDriverRow key={`extrinsic-${i}`} driver={d} type="extrinsic" color="blue" />
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderRetentionContent = () => {
    if (!diagnostic) return null;
    const trend = diagnostic.trend || [];

    return (
      <ZoomableTrendChart
        data={trend}
        title="Retention Rate Trend"
        testIdPrefix="retention"
        height={320}
        lines={[{ dataKey: 'rate', name: 'Retention %', stroke: '#10b981' }]}
      />
    );
  };

  const renderLEIContent = () => {
    if (!diagnostic) return null;
    const pillars = diagnostic.pillars || [];

    return (
      <div className="space-y-4">
        {pillars.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">LEI Pillars & Weights</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">Each pillar contributes to the overall LEI score based on its weight. Click the info icon to understand what each pillar measures and how it influences PE.</p>
              <div className="space-y-3">
                {pillars.map((p) => (
                  <LEIPillarRow key={p.name} pillar={p} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      );
    }

    if (loadError) {
      return (
        <Card className="border-rose-200 bg-rose-50/40" data-testid="diagnostic-error-state">
          <CardContent className="py-10 flex flex-col items-center text-center gap-3">
            <p className="text-sm font-medium text-rose-800">{loadError.message}</p>
            <p className="text-xs text-rose-600 max-w-md">
              {loadError.retriable
                ? 'This looks transient — the server may be busy or briefly unavailable. Retry should clear it.'
                : loadError.status === 401 || loadError.status === 403
                  ? 'Your session may have expired or you may not have access to this organization. Try logging out and back in, or switching the previewed org.'
                  : 'Try refreshing the page or switching tabs. If the problem persists the diagnostic API may be unavailable.'}
            </p>
            {loadError.retriable && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRetryNonce((n) => n + 1)}
                className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-100"
                data-testid="diagnostic-error-retry-btn"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Retry
              </Button>
            )}
            {loadError.status === 401 && (
              <Button
                size="sm"
                onClick={() => {
                  // Wipe both the auth token and any preview-org pin so
                  // the user lands on /login with a clean slate.
                  try {
                    secureStorage.removeItem('session_token');
                    sessionStorage.removeItem('viewAsOrg');
                  } catch (e) { /* ignore storage failures */ }
                  navigate('/login');
                }}
                className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="diagnostic-error-relogin-btn"
              >
                <LogOut className="h-3.5 w-3.5" /> Re-login
              </Button>
            )}
          </CardContent>
        </Card>
      );
    }

    if (!diagnostic) {
      return (
        <Card className="border-slate-200 bg-slate-50/40" data-testid="diagnostic-empty-state">
          <CardContent className="py-10 flex flex-col items-center text-center gap-2">
            <p className="text-sm font-medium text-slate-700">No diagnostic data available for this organization yet.</p>
            <p className="text-xs text-slate-500">Once assessments and program data are imported for the selected cohort, the {meta.label || activeTab} view will populate.</p>
          </CardContent>
        </Card>
      );
    }

    switch (activeTab) {
      case 'capability': return renderCapabilityContent();
      case 'motivation': return renderMotivationContent();
      case 'retention': return renderRetentionContent();
      case 'lei': return renderLEIContent();
      default: return null;
    }
  };

  const meta = componentMeta[activeTab] || {};

  const __pageTheme = getLightSectionTheme('PE');

  // Hero stats — the journey tiles (Capability → Visibility →
  // Confidence → Coordination) plus the Navigation Readiness value
  // tile, powered by /api/people-engine/health. Hides cleanly when
  // the data is missing — the deep-dive tabs below still render.
  const peCurrent = peHealth?.current || null;
  const peDelta = peHealth?.delta || {};

  const heroStats = peCurrent ? (
    <JourneyHeroStats current={peCurrent} delta={peDelta} onStageClick={setActiveTab} />
  ) : null;

  const heroActions = (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5 bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
      onClick={() => navigate('/people-engine/reference')}
      data-testid="pe-diag-reference-link"
    >
      <FileText className="h-4 w-4" /> Reference
    </Button>
  );

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Navigation Readiness Diagnostic"
        description="Identify where workforce capability is failing to convert into confidence, coordination, navigation, and value. The four journey tiles trace capability through the Missing Middle — Navigation Readiness is the value of that journey."
        theme={__pageTheme}
        icon={Compass}
        actions={heroActions}
        stats={heroStats}
        testId="pe-diag-hero"
      />
    )}>
    <div className="space-y-6" data-testid="pe-diagnostic-page">
      
      {/* Filters */}
      <div className="flex items-center gap-3">
        {isSuperAdmin && organizations.length > 0 && (
          <Select value={selectedOrg} onValueChange={(v) => { setSelectedOrg(v); setSelectedCohort('all'); }}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Select organization..." /></SelectTrigger>
            <SelectContent>
              {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={selectedCohort} onValueChange={setSelectedCohort}>
          <SelectTrigger className="w-56"><SelectValue placeholder="All Cohorts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cohorts</SelectItem>
            {cohorts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Journey Conversion Chain — where capability leaks before it becomes value */}
      {peCurrent && <JourneyConversionChain current={peCurrent} onStageClick={setActiveTab} />}

      {/* Navigation Readiness Trend + Missing Middle Friction Analysis */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2">
          <NavigationReadinessTrend trends={peHealth?.trends || []} />
        </div>
        <FrictionPanel orgId={selectedOrg} cohortId={selectedCohort} getHeaders={headers} />
      </div>

      {/* Journey Stage Deep-Dive */}
      <div className="pt-2">
        <h3 className="text-sm font-semibold text-slate-800">Journey Stage Deep-Dive</h3>
        <p className="text-xs text-slate-500 mt-0.5">Drill into the data stream behind each journey stage to find the root cause and launch a targeted intervention.</p>
      </div>

      {/* Description Card */}
      {diagnostic && (
        <Card className="border border-indigo-100 bg-indigo-50/30">
          <CardContent className="p-4 flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-700">{diagnostic.description}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono">{diagnostic.formula}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Component Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          {Object.entries(componentMeta).map(([key, m]) => (
            <TabsTrigger key={key} value={key} className="gap-1.5 text-xs" data-testid={`diag-tab-${key}`} title={`Driven by ${m.label}`}>
              <m.icon className="h-3.5 w-3.5" />
              {m.journey}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-4">{renderContent()}</div>
      </Tabs>

      {/* AI Narrative Section */}
      {!loading && diagnostic && (
        <Card className="border-2 border-violet-100" data-testid="ai-narrative-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-500" />
                AI Diagnostic Narrative
                <span className="text-xs font-normal text-slate-400">Powered by GPT-4o</span>
              </CardTitle>
              <div className="flex items-center gap-2">
                {narrative && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowNarrative(!showNarrative)}
                    className="gap-1 text-xs"
                  >
                    {showNarrative ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    {showNarrative ? 'Hide' : 'Show'}
                  </Button>
                )}
                <Button
                  variant={narrative ? "outline" : "default"}
                  size="sm"
                  onClick={generateNarrative}
                  disabled={narrativeLoading}
                  className="gap-1.5"
                  data-testid="generate-narrative-btn"
                >
                  {narrativeLoading ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  {narrative ? 'Regenerate' : 'Generate Analysis'}
                </Button>
              </div>
            </div>
          </CardHeader>
          {showNarrative && (
            <CardContent>
              {narrativeLoading ? (
                <div className="flex items-center gap-3 py-8 justify-center">
                  <RefreshCw className="h-5 w-5 animate-spin text-violet-500" />
                  <span className="text-sm text-slate-500">Generating AI analysis for {meta.label}...</span>
                </div>
              ) : narrative ? (
                <div className="prose prose-sm max-w-none">
                  <MarkdownRenderer text={narrative.narrative} />
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-400">
                    <span>Organization: {narrative.organization}</span>
                    <span>Cohort: {narrative.cohort}</span>
                    <span>Data points: {narrative.data_points}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 py-4 text-center">
                  Click "Generate Analysis" to get an AI-powered diagnostic with trend analysis, likely causes, and recommended interventions.
                </p>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Execution Impact drill-down */}
      <Dialog open={!!impactSkill} onOpenChange={(o) => { if (!o) { setImpactSkill(null); setImpactData(null); setReportCaseId(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="execution-impact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-violet-500" />
              Execution Impact — {impactSkill?.skill_name}
            </DialogTitle>
            {impactSkill && (
              <DialogDescription className="text-xs text-slate-500">
                Linked program: <span className="font-medium text-violet-700">{impactSkill.program_name}</span>
                <span className="text-slate-400 font-mono ml-1">{impactSkill.program_id}</span>
              </DialogDescription>
            )}
          </DialogHeader>
          {impactLoading ? (
            <div className="flex items-center gap-3 py-10 justify-center">
              <RefreshCw className="h-5 w-5 animate-spin text-violet-500" />
              <span className="text-sm text-slate-500">Analysing trajectory...</span>
            </div>
          ) : impactData ? (
            <div className="space-y-4">
              {/* Before/after delta card */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Before</div>
                  <div className="text-2xl font-bold text-slate-700 tabular-nums">
                    {impactData.before_after.before_avg_achieved?.toFixed(2) ?? '—'}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{impactData.before_after.before_periods} period{impactData.before_after.before_periods === 1 ? '' : 's'}</div>
                </div>
                <div className="p-3 rounded-md bg-violet-50 border border-violet-200">
                  <div className="text-xs text-violet-700 font-semibold uppercase tracking-wider">After linking</div>
                  <div className="text-2xl font-bold text-violet-900 tabular-nums">
                    {impactData.before_after.after_avg_achieved?.toFixed(2) ?? '—'}
                  </div>
                  <div className="text-xs text-violet-700 mt-0.5">{impactData.before_after.after_periods} period{impactData.before_after.after_periods === 1 ? '' : 's'}</div>
                </div>
                <div className={`p-3 rounded-md border ${
                  impactData.before_after.delta == null ? 'bg-slate-50 border-slate-200' :
                  impactData.before_after.delta > 0 ? 'bg-emerald-50 border-emerald-200' :
                  impactData.before_after.delta < 0 ? 'bg-rose-50 border-rose-200' :
                  'bg-slate-50 border-slate-200'
                }`}>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Delta</div>
                  <div className={`text-2xl font-bold tabular-nums ${
                    impactData.before_after.delta == null ? 'text-slate-500' :
                    impactData.before_after.delta > 0 ? 'text-emerald-700' :
                    impactData.before_after.delta < 0 ? 'text-rose-700' :
                    'text-slate-700'
                  }`}>
                    {impactData.before_after.delta == null ? '—' :
                      (impactData.before_after.delta > 0 ? '+' : '') + impactData.before_after.delta.toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {impactData.before_after.anchor_period ? `Anchor: ${impactData.before_after.anchor_period}` : 'No anchor yet'}
                  </div>
                </div>
              </div>

              {/* Combined chart */}
              {(impactData.skill_trend?.length > 0 || impactData.program_trend?.length > 0) ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Skill proficiency vs. Program score (by period)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DeferredChart height={260}>
                      <LineChart
                        data={(() => {
                          // Merge by period: left = skill (0-5), right = program (0-1 scaled to 0-100).
                          const byPeriod = {};
                          (impactData.skill_trend || []).forEach(r => {
                            byPeriod[r.period] = { period: r.period, achieved_level: r.achieved_level, required_level: r.required_level };
                          });
                          (impactData.program_trend || []).forEach(r => {
                            byPeriod[r.period] = { ...(byPeriod[r.period] || { period: r.period }), program_score_pct: Math.round(r.score * 100) };
                          });
                          return Object.values(byPeriod).sort((a, b) => a.period.localeCompare(b.period));
                        })()}
                        margin={{ top: 5, right: 50, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" domain={[0, 5]} tick={{ fontSize: 11 }} label={{ value: 'Achieved (0-5)', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#6366f1' } }} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} label={{ value: 'Program score (%)', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#8b5cf6' } }} />
                        <RechartsTooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {impactData.before_after.anchor_period && (
                          <ReferenceLine yAxisId="left" x={impactData.before_after.anchor_period} stroke="#64748b" strokeDasharray="4 4" label={{ value: 'Linked', fontSize: 10, fill: '#64748b', position: 'top' }} />
                        )}
                        <Line yAxisId="left" type="monotone" dataKey="achieved_level" name="Skill achieved" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                        <Line yAxisId="left" type="monotone" dataKey="required_level" name="Skill required" stroke="#cbd5e1" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="program_score_pct" name="Program score" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </DeferredChart>
                  </CardContent>
                </Card>
              ) : (
                <div className="py-8 text-center text-sm text-slate-500">
                  No trajectory data yet for this skill/program combination.
                </div>
              )}

              {/* Program meta */}
              <div className="text-xs text-slate-500 flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
                {impactData.program.start_date && <span><strong className="text-slate-700">Start:</strong> {String(impactData.program.start_date).slice(0, 10)}</span>}
                {impactData.program.end_date && <span><strong className="text-slate-700">End:</strong> {String(impactData.program.end_date).slice(0, 10)}</span>}
                {impactData.program.status && <span><strong className="text-slate-700">Status:</strong> {impactData.program.status}</span>}
                {impactData.program.target_measures?.length > 0 && (
                  <span><strong className="text-slate-700">Target measures:</strong> {impactData.program.target_measures.join(', ')}</span>
                )}
              </div>

              {/* Post to Executive Report Card */}
              <div className="pt-2 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant={reportCaseId ? 'outline' : 'default'}
                  className="gap-1.5"
                  onClick={postImpactToReportCard}
                  disabled={reportPosting || !!reportCaseId}
                  data-testid="post-to-report-card-btn"
                  title="Generate a CFO-ready executive summary and save it to the Business Case Builder"
                >
                  {reportCaseId
                    ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Posted</>
                    : reportPosting
                      ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Drafting summary...</>
                      : <><FileText className="h-3.5 w-3.5" /> Post to Executive Report Card</>}
                </Button>
                {reportCaseId && (
                  <>
                    <a
                      href="/business-case-builder"
                      className="text-xs text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
                      data-testid="open-report-card-link"
                    >
                      Open in Business Case Builder →
                    </a>
                    <Button
                      size="sm"
                      variant="default"
                      className="gap-1.5 bg-violet-600 hover:bg-violet-700"
                      onClick={() => setShareDialogOpen(true)}
                      data-testid="share-with-cfo-btn"
                      title="Generate a signed, read-only public link to share with your CFO/board"
                    >
                      <Share2 className="h-3.5 w-3.5" /> Share with your CFO
                    </Button>
                  </>
                )}
                <span className="text-xs text-slate-400 ml-auto">GPT-5.2 · CFO-ready summary</span>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Per-skill GPT-5.2 Action Plan dialog */}
      <Dialog open={!!planSkill} onOpenChange={(o) => { if (!o) { setPlanSkill(null); setPlanText(''); setPlanSavedId(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="skill-action-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Wand2 className="h-4 w-4 text-indigo-500" />
              Action Plan — {planSkill?.skill_name}
              <span className="text-xs font-normal text-slate-400 ml-1">GPT-5.2</span>
            </DialogTitle>
            {planSkill && (
              <DialogDescription className="text-xs text-slate-500">
                Current {planSkill.achieved_level?.toFixed(1)} / Required {planSkill.required_level?.toFixed(1)} ·
                gap of {Math.max(0, (planSkill.required_level || 0) - (planSkill.achieved_level || 0)).toFixed(2)} points
              </DialogDescription>
            )}
          </DialogHeader>
          {planLoading ? (
            <div className="flex items-center gap-3 py-10 justify-center">
              <RefreshCw className="h-5 w-5 animate-spin text-indigo-500" />
              <span className="text-sm text-slate-500">Drafting intervention for {planSkill?.skill_name}...</span>
            </div>
          ) : (
            <>
              <div className="prose prose-sm max-w-none pt-2">
                <MarkdownRenderer text={planText} />
              </div>
              {planText && (
                <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-slate-100">
                  <Button
                    size="sm"
                    variant={planSavedId ? "outline" : "default"}
                    className="gap-1.5"
                    onClick={savePlanToBacklog}
                    disabled={planSaving || !!planSavedId}
                    data-testid="plan-save-backlog-btn"
                  >
                    {planSavedId
                      ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Saved to Backlog</>
                      : planSaving
                        ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Saving...</>
                        : <><Save className="h-3.5 w-3.5" /> Save to Program Backlog</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={printPlan}
                    data-testid="plan-print-btn"
                  >
                    <Printer className="h-3.5 w-3.5" /> Print
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={exportPlanMarkdown}
                    data-testid="plan-export-btn"
                  >
                    <Download className="h-3.5 w-3.5" /> Export .md
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Report Card dialog (reused from LIE rollup) */}
      <ShareReportCardDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        report={reportCaseId ? { case_id: reportCaseId, skill_name: impactSkill?.skill_name } : null}
      />
    </div>
    </PageShell>
  );
}
